import { useEffect, useState, useCallback, useId } from "react";
import { PlatformManagerService } from "@/lib/platformManager/platformManager.service";
import type {
	UserWithBalance,
	UserDetails,
} from "@/lib/platformManager/platformManager.service";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Minus, Eye, ArrowLeft } from "lucide-react";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PlatformManagerUserDetailsComponent } from "./components/platformManagerUserDetails.component";

export default function PlatformManagerUsersPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [users, setUsers] = useState<UserWithBalance[]>([]);
	const [loading, setLoading] = useState(true);

	// Coin distribution modal state
	const [coinModalOpen, setCoinModalOpen] = useState(false);
	const [coinModalType, setCoinModalType] = useState<"add" | "deduct">("add");
	const [selectedUser, setSelectedUser] = useState<UserWithBalance | null>(
		null,
	);
	const [coinAmount, setCoinAmount] = useState("");
	const [coinDescription, setCoinDescription] = useState("");
	const [coinLoading, setCoinLoading] = useState(false);

	// User details modal state
	const [userDetailsModalOpen, setUserDetailsModalOpen] = useState(false);
	const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
	const [loadingUserDetails, setLoadingUserDetails] = useState(false);

	const platformManagerService = PlatformManagerService.getInstance();
	const amountInputId = useId();
	const descriptionInputId = useId();

	// Format date helper
	const formatDate = (dateString: string | null) => {
		if (!dateString) return t("common.never");
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	// Load data function
	const loadData = useCallback(async () => {
		try {
			setLoading(true);
			const usersData = await platformManagerService.getAllUsers();
			setUsers(usersData);
		} catch (error) {
			console.error("Failed to load users:", error);
			toast.error(t("platformManager.errors.loadFailed"));
		} finally {
			setLoading(false);
		}
	}, [platformManagerService, t]);

	// Load data on mount
	useEffect(() => {
		loadData();
	}, [loadData]);

	// Open coin distribution modal
	const openCoinModal = (user: UserWithBalance, type: "add" | "deduct") => {
		setSelectedUser(user);
		setCoinModalType(type);
		setCoinAmount("");
		setCoinDescription("");
		setCoinModalOpen(true);
	};

	const handleCoinDistribution = async () => {
		if (!selectedUser || !coinAmount) {
			toast.error(t("platformManager.coinDistribution.enterAmount"));
			return;
		}

		const amount = Number.parseInt(coinAmount);
		if (Number.isNaN(amount) || amount <= 0) {
			toast.error(t("platformManager.coinDistribution.enterValidAmount"));
			return;
		}

		try {
			setCoinLoading(true);
			if (coinModalType === "add") {
				await platformManagerService.addCoins(
					selectedUser.id,
					amount,
					coinDescription || undefined,
				);
				toast.success(
					t("platformManager.coinDistribution.coinsAdded", {
						amount,
						email: selectedUser.email,
					}),
				);
			} else {
				await platformManagerService.deductCoins(
					selectedUser.id,
					amount,
					coinDescription || undefined,
				);
				toast.success(
					t("platformManager.coinDistribution.coinsDeducted", {
						amount,
						email: selectedUser.email,
					}),
				);
			}
			setCoinModalOpen(false);
			loadData(); // Reload data to reflect changes
		} catch (error) {
			console.error("Failed to distribute coins:", error);
			toast.error(t("platformManager.coinDistribution.distributionFailed"));
		} finally {
			setCoinLoading(false);
		}
	};

	// Open user details modal
	const openUserDetailsModal = async (user: UserWithBalance) => {
		setSelectedUser(user);
		setUserDetailsModalOpen(true);
		setLoadingUserDetails(true);

		try {
			const details = await platformManagerService.getUserDetails(user.id);
			setUserDetails(details);
		} catch (error) {
			console.error("Failed to load user details:", error);
			toast.error(t("platformManager.errors.loadUserDetailsFailed"));
			setUserDetailsModalOpen(false);
		} finally {
			setLoadingUserDetails(false);
		}
	};

	// Define table columns
	const columns: DataTableColumn<UserWithBalance>[] = [
		{
			key: "email",
			label: t("platformManager.userManagement.columns.email"),
			sortable: true,
			accessor: (user) => user.email,
			render: (user) => <span className="font-medium">{user.email}</span>,
		},
		{
			key: "name",
			label: t("platformManager.userManagement.columns.name"),
			sortable: true,
			accessor: (user) => user.name || "",
			render: (user) => user.name || "-",
		},
		{
			key: "coinBalance",
			label: t("platformManager.userManagement.columns.balance"),
			sortable: true,
			accessor: (user) => user.coinBalance,
			render: (user) => (
				<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
					{user.coinBalance.toLocaleString()}
				</span>
			),
		},
		{
			key: "podcastCount",
			label: t("platformManager.userManagement.columns.podcasts"),
			sortable: false,
			accessor: (user) => user.podcastCount,
		},
		{
			key: "lastActive",
			label: t("platformManager.userManagement.columns.lastActive"),
			sortable: true,
			accessor: (user) => user.lastActive || "",
			render: (user) => formatDate(user.lastActive),
		},
		{
			key: "createdAt",
			label: t("platformManager.userManagement.columns.joined"),
			sortable: true,
			accessor: (user) => user.createdAt,
			render: (user) => formatDate(user.createdAt),
		},
		{
			key: "actions",
			label: t("platformManager.userManagement.columns.actions"),
			sortable: false,
			render: (user) => (
				<div className="flex gap-2">
					<Button
						size="sm"
						variant="outline"
						onClick={() => openUserDetailsModal(user)}
						title={t("platformManager.tooltips.viewDetails")}
					>
						<Eye className="h-4 w-4" />
					</Button>
					<Button
						size="sm"
						variant="outline"
						onClick={() => openCoinModal(user, "add")}
						title={t("platformManager.tooltips.addCoins")}
					>
						<Plus className="h-4 w-4" />
					</Button>
					<Button
						size="sm"
						variant="outline"
						onClick={() => openCoinModal(user, "deduct")}
						title={t("platformManager.tooltips.deductCoins")}
					>
						<Minus className="h-4 w-4" />
					</Button>
				</div>
			),
		},
	];

	return (
		<div className="container mx-auto p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="icon"
					onClick={() => navigate("/platform-manager")}
				>
					<ArrowLeft className="h-5 w-5" />
				</Button>
				<div>
					<h1 className="text-3xl font-bold">
						{t("platformManager.userManagement.title")}
					</h1>
					<p className="text-gray-600">
						{t("platformManager.userManagement.description")}
					</p>
				</div>
			</div>

			{/* Users Table */}
			<DataTable
				data={users}
				columns={columns}
				title={t("platformManager.userManagement.tableTitle")}
				description={t("platformManager.userManagement.tableDescription")}
				searchable
				searchPlaceholder={t(
					"platformManager.userManagement.searchPlaceholder",
				)}
				defaultSortField="createdAt"
				defaultSortOrder="desc"
				loading={loading}
				emptyMessage={t("platformManager.userManagement.noUsersFound")}
			/>

			{/* User Details Modal */}
			{userDetailsModalOpen && userDetails && (
				<PlatformManagerUserDetailsComponent
					userDetails={userDetails}
					userDetailsModalOpen={userDetailsModalOpen}
					setUserDetailsModalOpen={setUserDetailsModalOpen}
					loadingUserDetails={loadingUserDetails}
				/>
			)}

			{/* Coin Distribution Modal */}
			<Dialog open={coinModalOpen} onOpenChange={setCoinModalOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{coinModalType === "add"
								? t("platformManager.coinDistribution.addCoins")
								: t("platformManager.coinDistribution.deductCoins")}
						</DialogTitle>
						<DialogDescription>
							{coinModalType === "add"
								? t("platformManager.coinDistribution.addCoinsTo")
								: t("platformManager.coinDistribution.deductCoinsFrom")}{" "}
							{selectedUser?.email}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label htmlFor={amountInputId}>
								{t("platformManager.coinDistribution.amount")}
							</Label>
							<Input
								id={amountInputId}
								type="number"
								placeholder={t(
									"platformManager.coinDistribution.amountPlaceholder",
								)}
								value={coinAmount}
								onChange={(e) => setCoinAmount(e.target.value)}
								min="1"
							/>
						</div>
						<div>
							<Label htmlFor={descriptionInputId}>
								{t("platformManager.coinDistribution.descriptionLabel")}
							</Label>
							<Input
								id={descriptionInputId}
								placeholder={t(
									"platformManager.coinDistribution.descriptionPlaceholder",
								)}
								value={coinDescription}
								onChange={(e) => setCoinDescription(e.target.value)}
							/>
						</div>
						<div className="text-sm text-gray-600">
							{t("platformManager.coinDistribution.currentBalance", {
								balance: selectedUser?.coinBalance.toLocaleString(),
							})}
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setCoinModalOpen(false)}
							disabled={coinLoading}
						>
							{t("common.cancel")}
						</Button>
						<Button onClick={handleCoinDistribution} disabled={coinLoading}>
							{coinLoading
								? t("common.processing")
								: coinModalType === "add"
									? t("platformManager.coinDistribution.addCoins")
									: t("platformManager.coinDistribution.deductCoins")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
